/**
 * LeadTagChips - read-only display of a lead's credential tags.
 *
 * Resolves slug values to their catalog viewValue labels (TD-041). Formal
 * credentials get a subtle emphasis. Caps the visible count with a "+N".
 */

import React from "react";
import type { LeadTag } from "../Lead.types";

interface LeadTagChipsProps {
  tags?: string[];
  catalog: LeadTag[];
  limit?: number;
}

export const LeadTagChips: React.FC<LeadTagChipsProps> = ({
  tags,
  catalog,
  limit = 3,
}) => {
  if (!tags || tags.length === 0) return null;

  const byValue = new Map(catalog.map((t) => [t.value, t]));
  // Credentials first, then anything else; skip unknown-but-keep as raw slug.
  const resolved: LeadTag[] = tags
    .map(
      (slug): LeadTag =>
        byValue.get(slug) || {
          value: slug,
          viewValue: slug,
          category: "",
          isFormal: false,
          url: null,
        }
    )
    .filter((t) => t.category !== "tier");

  if (resolved.length === 0) return null;

  const shown = resolved.slice(0, limit);
  const extra = resolved.length - shown.length;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {shown.map((t) => {
        const url = t.url;
        const base = `inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium ${
          t.isFormal
            ? "bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200"
            : "bg-gray-100 text-gray-600"
        }`;
        // Official tags with a certification link open the body's page; stop
        // propagation so clicking the chip does not also open the lead row.
        return url ? (
          <a
            key={t.value}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            title={`${t.viewValue} — apri la certificazione`}
            onClick={(e) => e.stopPropagation()}
            className={`${base} hover:underline hover:ring-emerald-300 cursor-pointer`}
          >
            {t.viewValue}
            <span aria-hidden className="opacity-60">↗</span>
          </a>
        ) : (
          <span key={t.value} title={t.viewValue} className={base}>
            {t.viewValue}
          </span>
        );
      })}
      {extra > 0 && (
        <span className="text-[10px] text-gray-400">+{extra}</span>
      )}
    </div>
  );
};
